import { Lender } from '@/models/registry';
import type { Lender as LenderType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import type { BaseStore } from './base';

export class LenderStore implements BaseStore<Lender, LenderType> {
  store: Map<string, Lender> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  getById(id: string): Lender | undefined {
    return this.store.get(id);
  }

  add(input: LenderType) {
    const lender = new Lender(input);
    this.store.set(lender.id, lender);
  }

  // Computed getters - automatically update when store changes
  get all() {
    return Array.from(this.store.values());
  }

  private setLenderField = <K extends keyof LenderType>(
    lender: Lender,
    key: K,
    value: LenderType[K],
  ) => {
    (lender as Pick<Lender, K>)[key] = value as Lender[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<LenderType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.lenders.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.lenders.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.lenders.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.lenders.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<LenderType>;
  }) {
    const existingLender = this.getById(delta.record_id);
    if (!existingLender) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof LenderType,
      LenderType[keyof LenderType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingLender[key] === newValue) {
        continue;
      }

      this.setLenderField(existingLender, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingLender);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<LenderType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<LenderType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.lenders.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.record_id);
    }

    if (delta.action === 'archive') {
      this.remove(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  upsert(lender: LenderType) {
    const existingLender = this.getById(lender.id);
    if (existingLender) {
      this.replace({ id: lender.id, lender });
    } else {
      this.add(lender);
    }
  }

  replace({ id, lender }: { id: string; lender: LenderType }) {
    const existingLender = this.getById(id);
    if (!existingLender) return;

    existingLender.name = lender.name;
    existingLender.phone = lender.phone;
    existingLender.email = lender.email;
    existingLender.lender_type = lender.lender_type;

    this.save(existingLender);
  }

  save(lender: Lender) {
    this.store.set(lender.id, lender);
  }

  remove(id: string) {
    this.store.delete(id);

    // remove from dexie
    db.lenders.delete(id);
  }
}

export const lenderStore = new LenderStore();
